import {
    getGamedayBoxUrl,
    getGamedayUrl,
    getGamedayPreviewUrl,
    getGamedayVideoUrl,
    getGamedayFinalVideoUrl,
    getGamedayWrapUrl
} from './getGamedayUrls';
import getTvUrl from './getTvUrl';
import getMilbTvUrl from './getMilbTvUrl';
import { ButtonStyles } from '@mlb/react-patterns/v4/components/Button/button.types';
import { getBroadcastsConfigState } from '../store/selectors/broadcastsConfigSelector';
import { getCustomProductButtonsConfigState } from '../store/selectors/customProductButtonsConfigSelector';
import { getAppContextConfigState } from '../store/selectors/getAppContextSelector';
import { useSelector } from 'react-redux';
import getPartnerBroadcasterConfig from './getPartnerBroadcasterConfig';

const { LINK_BLUE, TICKET_LINK_GREEN } = ButtonStyles;

const DESKTOP = 'desktop';
const MOBILE = 'mobile';

const youTubeFgowFallback = 'https://www.mlb.com/live-stream-games/youtube';
const appleTvUrlFallback = 'https://apple.co/mlb';

const isArray = Array.isArray;

const previewButton = {
    id: 'preview',
    label: 'preview',
    icon: 'icon-preview',
    url: (urlRoot, gamePk) => getGamedayPreviewUrl(urlRoot, gamePk),
    style: LINK_BLUE
};

// TODO there is some "tfl" link query string parameter to indicate the traffic is coming from Scoreboard TBD
const ticketsButton = {
    id: 'tickets',
    icon: 'icon-ticket',
    scheduleIcon: 'schedule-tickets',
    label: 'tickets',
    url: (ticketsLink) => `${ticketsLink}`,
    style: TICKET_LINK_GREEN,
    target: '_blank'
};

const watchButton = {
    id: 'watch',
    icon: 'icon-tv',
    label: 'button_watch',
    url: (urlRoot, gamePk, gameDate) => getTvUrl(urlRoot, gamePk, gameDate),
    style: LINK_BLUE
};

const watchPreGameShowButton = {
    id: 'watchPreGameShow',
    icon: 'icon-tv',
    label: 'button_watch',
    url: (urlRoot, gamePk, gameDate) => getTvUrl(urlRoot, gamePk, gameDate),
    style: LINK_BLUE
};

const watchMilbButton = {
    id: 'watchMilb',
    icon: 'icon-tv',
    label: 'button_milbtv',
    url: (urlRoot, gamePk, gameDate, { isApp, deepLinkPrefix }) => getMilbTvUrl({ urlRoot, gamePk, isApp, deepLinkPrefix }),
    style: LINK_BLUE
};

const youTubeButton = {
    id: 'youTube',
    icon: 'youtube',
    label: 'button_youtube',
    url: (youTubeUrl) => youTubeUrl || youTubeFgowFallback,
    style: '#F00',
    watchButton
};

const appleTvButton = {
    id: 'appleTv',
    icon: 'icon-tv',
    label: 'button_watch',
    url: (appleTvUrl) => appleTvUrl || appleTvUrlFallback, // For now, default to appleTv home page
    style: LINK_BLUE,
    watchButton
};

const liveBoxButton = {
    id: 'button_live_box',
    icon: 'box',
    label: 'button_live_box',
    url: (urlRoot, gamePk) => getGamedayUrl(urlRoot, gamePk),
    style: LINK_BLUE
};

const gamedayButton = {
    id: 'gameday',
    label: 'gameday',
    icon: 'icon-gameday-2',
    url: (urlRoot, gamePk) => getGamedayUrl(urlRoot, gamePk),
    style: LINK_BLUE,
    liveBoxButton
};

const highlightsButton = {
    id: 'highlights',
    label: 'highlights',
    icon: 'icon-highlight',
    url: (urlRoot, gamePk) => getGamedayVideoUrl(urlRoot, gamePk),
    style: LINK_BLUE
};

const highlightsFinalButton = {
    id: 'highlights',
    label: 'highlights',
    icon: 'icon-highlight',
    url: (urlRoot, gamePk) => getGamedayFinalVideoUrl(urlRoot, gamePk),
    style: LINK_BLUE
};

const wrapButton = {
    id: 'wrap',
    label: 'wrap',
    icon: 'icon-wrap',
    url: (urlRoot, gamePk) => getGamedayWrapUrl(urlRoot, gamePk),
    style: LINK_BLUE
};

const boxButton = {
    id: 'box',
    icon: 'icon-box',
    label: 'box',
    url: (urlRoot, gamePk) => getGamedayBoxUrl(urlRoot, gamePk),
    style: LINK_BLUE
};

const storyButton = {
    id: 'story',
    icon: 'icon-gamestory-hollow',
    label: 'button_story',
    url: (storyUrl) => storyUrl ? `https://${storyUrl}` : '',
    style: LINK_BLUE,
    target: '_blank'
};

const partnerBroadcasterButton = (freeGameList) => {
    const {
        url,
        icon,
        buttonLabelKey
    } = freeGameList || {};

    return {
        id: 'partner',
        icon,
        label: buttonLabelKey || '',
        url: () => (url || ''),
        style: '#F00',
        watchButton
    };
};

const buttonMap = {
    preview: () => previewButton,
    tickets: () => ticketsButton,
    watch: ({ isYouTubeFGOW, isAppleTv, freeGameList, appContext }) => {
        // keep isYouTubeFGOW and isAppleTv to backward compatible for MIG and SNS
        if (freeGameList) {
            return partnerBroadcasterButton(freeGameList);
        } else if (isYouTubeFGOW) {
            return youTubeButton;
        } else if (isAppleTv) {
            return appleTvButton;
        } else if (appContext === 'milb') {
            return watchMilbButton;
        }

        return watchButton;
    },
    watchPreGameShow: () => watchPreGameShowButton,
    gameday: () => gamedayButton,
    highlights: ({ gameState }) => gameState === 'Final' ? highlightsFinalButton : highlightsButton,
    wrap: () => wrapButton,
    box: () => boxButton,
    story: () => storyButton
};

// can import these where customProductButtons are defined
export const buttonTypes = {
    PREVIEW: 'preview',
    TICKETS: 'tickets',
    WATCH: 'watch',
    WATCHPREGAMESHOW: 'watchPreGameShow',
    GAMEDAY: 'gameday',
    HIGHLIGHTS: 'highlights',
    WRAP: 'wrap',
    BOX: 'box',
    STORY: 'story'
};

export const cardTypes = {
    SCOREBOARD: 'Scoreboard',
    SCHEDULE: 'Schedule',
    MINI_SB: 'Mini-SB',
    MINI_SB_INFINITY: 'Mini-SB-Infinity',
    MIG: 'Mig',
    SCHEDULE_RIBBON: 'Schedule-Ribbon',
    SCHEDULE_RIBBON_TICKETS: 'Schedule-Ribbon-Tickets'
};

// Configs for displaying the various button
// formatting types. All apps show the YouTube
// icon, so that is separate from `showIcons`.
export const buttonDisplayFormatConfig = {
    Mig: {
        showIcons: true,
        showLabel: true,
        affiliateId: 'MIG'
    },
    'Mini-SB': {
        showIcons: true,
        showLabel: true,
        buttonStyle: ButtonStyles.DEFAULT,
        affiliateId: 'MINISB'
    },
    'Mini-SB-Infinity': {
        showIcons: false,
        showLabel: true,
        buttonStyle: ButtonStyles.DEFAULT,
        affiliateId: 'MINISB',
        buttonLimit: 2
    },
    Scoreboard: {
        showIcons: false,
        showLabel: true,
        affiliateId: 'SCORES'
    },
    Schedule: {
        showIcons: true,
        showLabel: false,
        affiliateId: 'SCHEDULE'
    },
    'Schedule-Ribbon': {
        showIcons: false,
        showLabel: true,
        affiliateId: 'SCHEDULE_RIBBON',
        buttonLimit: 2
    },
    'Schedule-Ribbon-Tickets': {
        showIcons: false,
        showLabel: true,
        affiliateId: 'SCHEDULE_RIBBON',
        buttonLimit: 2
    }
};
const emptyButtonSet = {
    Preview: [],
    Live: [],
    Final: []
};

// Ticket logic is handled on backend and the tickets obj is avail (or not) based on it so it will live in all game states. Per Product
// The key pattern is device lowercase cardType or lowercase cardType + condensed when condensed is request.
const buttonConfigs = {
    // SCOREBOARD
    scoreboard: {
        desktop: {
            Preview: ['watch', 'preview', 'gameday', 'tickets', 'story'],
            Live: ['watch', 'gameday', 'tickets', 'story'],
            Final: ['watch', 'preview', 'wrap', 'box', 'story']
        },
        mobile: {
            Preview: ['watch', 'preview', 'gameday', 'tickets', 'story'],
            Live: ['watch', 'gameday', 'tickets', 'story'],
            Final: ['watch', 'preview', 'wrap', 'box', 'story']
        }
    },
    'scoreboard-condensed': {
        mobile: emptyButtonSet
    },
    // SCHEDULE
    schedule: {
        desktop: {
            Preview: ['tickets'],
            Live: ['tickets'],
            Final: []
        },
        mobile: {
            Preview: ['tickets'],
            Live: ['tickets'],
            Final: []
        }
    },
    'schedule-postseason': {
        desktop: {
            Preview: ['watch', 'tickets'],
            Live: ['watch', 'tickets'],
            Final: ['watch']
        },
        mobile: {
            Preview: ['watch', 'tickets'],
            Live: ['watch', 'tickets'],
            Final: ['watch']
        }
    },
    'mini-sb': {
        desktop: {
            Preview: ['watch', 'preview', 'gameday', 'tickets'],
            Live: ['watch', 'gameday', 'highlights', 'tickets'],
            Final: ['watch', 'preview', 'wrap', 'box', 'highlights']
        },
        mobile: emptyButtonSet
    },
    'mini-sb-infinity': {
        desktop: {
            Preview: ['preview', 'gameday', 'watchPreGameShow', 'story'],
            Live: ['gameday', 'watch'],
            Final: ['preview', 'wrap', 'watch']
        },
        mobile: emptyButtonSet
    },
    // MIG
    mig: {
        desktop: {
            Preview: ['watch', 'preview', 'gameday', 'tickets', 'story'],
            Live: ['watch', 'gameday', 'tickets', 'story'],
            Final: ['watch', 'wrap', 'box', 'story']
        },
        mobile: {
            Preview: ['watch', 'preview', 'gameday', 'tickets', 'story'],
            Live: ['watch', 'gameday', 'tickets', 'story'],
            Final: ['watch', 'wrap', 'box', 'story']
        }
    },
    'schedule-ribbon': {
        desktop: {
            Preview: ['preview', 'gameday', 'tickets'],
            Live: ['gameday', 'tickets'],
            Final: ['wrap']
        },
        mobile: {
            Preview: ['preview'],
            Live: ['gameday'],
            Final: ['wrap']
        }
    },
    'schedule-ribbon-tickets': {
        desktop: {
            Preview: ['tickets'],
            Live: ['gameday'],
            Final: ['wrap']
        },
        mobile: {
            Preview: ['tickets'],
            Live: ['gameday'],
            Final: ['wrap']
        }
    }
};

const retrieveDesktopOrMobile = (group = {}, isMobile) => {
    if (isArray(group)) {
        return group;
    }
    return group[(isMobile ? MOBILE : DESKTOP)] || group.desktop || emptyButtonSet;
};

// Returns the correct button config based on gameState, mobile, condensed, etc.
const retrieveButtonConfig = ({
    isMobile = false,
    cardType = '',
    gameState,
    variant = '',
    isYouTubeFGOW,
    isAppleTv,
    epglist,
    epgContentList
}) => {
    const appContext = useSelector(getAppContextConfigState);
    const broadcastConfig = useSelector(getBroadcastsConfigState) || {};
    const { customProductButtons } = useSelector(getCustomProductButtonsConfigState) ?? {};
    const { epg: epgConfig, useContentEpg } = broadcastConfig;
    const lowerCardType = cardType.toLowerCase();
    const group = customProductButtons ?? (buttonConfigs[`${lowerCardType}${variant ? '-' + variant : ''}`] || buttonConfigs[lowerCardType]);
    const buttonsArr = retrieveDesktopOrMobile(group, isMobile)[gameState];
    const freeGameList = getPartnerBroadcasterConfig({ epglist, epgContentList, epgConfig, useContentEpg });
    return buttonsArr?.map((btn) => buttonMap?.[btn]({ isMobile, gameState, isYouTubeFGOW, isAppleTv, freeGameList, appContext }));
};

export default retrieveButtonConfig;
